import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LogOut, Package, Plus, Search, ShoppingCart, Store as StoreIcon, User } from 'lucide-react';

import { ApiError } from '@/api/client';
import { patientApi } from '@/api/patientClient';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PatientSessionProvider, usePatientSession } from './patientSession';
import { useCart } from './cart';
import { CartDrawer } from './CartDrawer';
import type { PatientCatalogItem } from './types';

interface StoreInfo {
  storeName: string;
  deliveryFee: number;
  minOrderAmount: number;
}

function money(n: number) {
  return `₹${n.toFixed(2)}`;
}

export function StorePage() {
  return (
    <PatientSessionProvider>
      <StorePageInner />
    </PatientSessionProvider>
  );
}

function StorePageInner() {
  const { pharmacySlug = '' } = useParams();
  const { patient, isAuthenticated, logout } = usePatientSession();
  const cart = useCart(pharmacySlug);

  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [items, setItems] = useState<PatientCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    patientApi
      .get<StoreInfo>(`/api/store/${pharmacySlug}`, undefined, true)
      .then(setStoreInfo)
      .catch((err) => {
        setStoreError(
          err instanceof ApiError
            ? err.code === 'ORDERING_DISABLED'
              ? "This pharmacy isn't taking online orders right now."
              : err.message
            : 'Could not load this store.',
        );
      });
  }, [pharmacySlug]);

  // Debounced the same way the staff app's own search boxes are — a round
  // trip per keystroke is wasteful, and the catalog is typically small
  // enough that a short debounce feels instant.
  useEffect(() => {
    if (!storeInfo) return;
    setLoading(true);
    const handle = setTimeout(() => {
      patientApi
        .get<PatientCatalogItem[]>(`/api/store/${pharmacySlug}/catalog`, { search: search || undefined }, true)
        .then(setItems)
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [pharmacySlug, search, storeInfo]);

  const cartQuantities = useMemo(
    () => new Map(cart.lines.map((l) => [l.medicineId, l.quantity])),
    [cart.lines],
  );

  if (storeError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center">
          <StoreIcon className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{storeError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-grad-gold font-display text-lg font-bold text-ink-950">
              {storeInfo?.storeName?.[0] ?? '·'}
            </div>
            <div className="min-w-0">
              <p className="truncate font-display text-base leading-tight">
                {storeInfo?.storeName ?? <Skeleton className="h-4 w-32" />}
              </p>
              <p className="text-xs text-muted-foreground">Order online</p>
            </div>
          </div>

          {isAuthenticated ? (
            <button
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary"
            >
              <User className="h-3.5 w-3.5" />
              {patient?.name || patient?.phone}
              <LogOut className="h-3 w-3" />
            </button>
          ) : null}
        </div>

        <div className="mx-auto max-w-2xl px-4 pb-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Search medicines" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <Package className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              {search ? `No matches for "${search}".` : 'Nothing available to order right now.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {items.map((item) => (
              <div key={item.medicineId} className="flex flex-col rounded-2xl border border-border/60 bg-card p-3">
                <p className="line-clamp-2 min-h-[2.5rem] text-sm font-medium">{item.name}</p>
                {item.genericName ? (
                  <p className="line-clamp-1 text-xs text-muted-foreground">{item.genericName}</p>
                ) : null}
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">{money(item.price)}</span>
                  <span className="text-[11px] text-muted-foreground">/{item.unit}</span>
                </div>
                {!item.inStock ? (
                  <Badge variant="out" className="mt-2 self-start">
                    Out of stock
                  </Badge>
                ) : (
                  <button
                    onClick={() => cart.addItem(item)}
                    className="mt-2 flex items-center justify-center gap-1 rounded-lg bg-primary/10 py-1.5 text-xs font-semibold text-primary hover:bg-primary/15"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {cartQuantities.get(item.medicineId) ? `In cart · ${cartQuantities.get(item.medicineId)}` : 'Add'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {cart.itemCount > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed inset-x-4 bottom-4 z-20 mx-auto flex max-w-2xl items-center justify-between rounded-2xl bg-primary px-5 py-3.5 text-primary-foreground shadow-glow-gold"
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <ShoppingCart className="h-4 w-4" />
            {cart.itemCount} item{cart.itemCount === 1 ? '' : 's'}
          </span>
          <span className="text-sm font-semibold">
            {money(cart.subtotal + cart.gstAmount + (storeInfo?.deliveryFee ?? 0))}
          </span>
        </button>
      )}

      {storeInfo ? (
        <CartDrawer
          pharmacySlug={pharmacySlug}
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          deliveryFee={storeInfo.deliveryFee}
          minOrderAmount={storeInfo.minOrderAmount}
        />
      ) : null}
    </div>
  );
}
